import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, setDoc, query, where, orderBy, onSnapshot, getDocs, Timestamp } from "firebase/firestore";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  sendPasswordResetEmail,
  updateProfile
} from "firebase/auth";
import { Project } from './types';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDHYkGvcC54Zl6RyCFtShNeyU0ppaRRd4A",
  authDomain: "eisenhowerish.firebaseapp.com",
  projectId: "eisenhowerish",
  storageBucket: "eisenhowerish.firebasestorage.app",
  messagingSenderId: "103805684765",
  appId: "1:103805684765:web:695b3fad8e36c9fa168af4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

// Auth providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();

// Project collection reference
export const projectsCollection = collection(db, 'projects');

/**
 * Map a Firestore document to a Project.
 *
 * Shared by the live subscription and the one-shot read so the timestamp
 * conversions cannot drift apart. Typed loosely because the callers pass a
 * QueryDocumentSnapshot and a DocumentSnapshot, which differ structurally.
 */
export function projectFromDoc(
  snapshot: { id: string; data: () => Record<string, unknown> },
): Project {
  const data = snapshot.data() as Record<string, { toDate?: () => Date } | unknown>

  /**
   * Convert a Firestore field to a Date. Absent/null/undefined means "no
   * value" and yields undefined -- load-bearing for lastWorkedAt, where
   * undefined means "no seed" and lets scoring fall back to createdAt.
   *
   * But a value that IS present and NOT a Timestamp (a hand-edited string in
   * the console, an import, a migration) must throw rather than silently
   * fall through to `undefined`/`new Date()`. daysSinceWorked falls back to
   * createdAt when lastWorkedAt is absent, so a string createdAt used to
   * read as "created right now" on every load -- permanent zero neglect,
   * nothing on screen to reveal it. Per src/lib/dates.ts's policy: "A seed
   * that is quietly wrong is worse than one that is refused."
   */
  const toDate = (field: string, v: unknown): Date | undefined => {
    if (v === undefined || v === null) return undefined
    if (typeof (v as { toDate?: unknown }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate()
    }
    throw new Error(`projectFromDoc: field "${field}" is present but not a Firestore Timestamp: ${JSON.stringify(v)}`)
  }

  return {
    ...(data as unknown as Project),
    id: snapshot.id,
    createdAt: toDate('createdAt', data.createdAt) ?? new Date(),
    updatedAt: toDate('updatedAt', data.updatedAt) ?? new Date(),
    // Left undefined when absent so scoring falls back to createdAt.
    lastWorkedAt: toDate('lastWorkedAt', data.lastWorkedAt),
  }
}

// Firebase service functions
export const firebaseService = {
  // Authentication methods
  async signInWithEmail(email: string, password: string) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  },

  async signUpWithEmail(email: string, password: string, displayName?: string) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name if provided
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }
      
      return result.user;
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  },

  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  },

  async signInWithFacebook() {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      return result.user;
    } catch (error) {
      console.error('Error signing in with Facebook:', error);
      throw error;
    }
  },

  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw error;
    }
  },

  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  // Project CRUD operations
  async createProject(projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const project = {
        ...projectData,
        userId: user.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(projectsCollection, project);
      return { id: docRef.id, ...project };
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  async updateProject(projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'userId'>>) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectRef = doc(db, 'projects', projectId);
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
      };

      await updateDoc(projectRef, updateData);
      return { id: projectId, ...updateData };
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  /**
   * Record that real work happened on a project today — the decay clock.
   *
   * Deliberately separate from `updateProject`: editing a project's sliders
   * must NOT reset its neglect timer, or tinkering with the app would make
   * neglect disappear. Only this deliberate action moves `lastWorkedAt`.
   */
  async markWorkedOn(projectId: string) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const workedAt = Timestamp.now();
      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, { lastWorkedAt: workedAt, updatedAt: workedAt });
      return workedAt.toDate();
    } catch (error) {
      console.error('Error marking project worked on:', error);
      throw error;
    }
  },

  /**
   * The only telemetry in this app. Added so §6.3 of the decay
   * pre-registration (usage survival — "opened on ≥14 of 21 days") is
   * measurable at all; there is otherwise no record that the app was ever
   * opened. One document per user per local day, idempotent within a day.
   *
   * Failures are swallowed: instrumentation must never break the app it is
   * measuring, and a missing open-record is a far smaller problem than a
   * crashed launch during the window.
   */
  async recordAppOpen(userId: string) {
    try {
      const now = new Date();
      const day = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-');

      await setDoc(
        doc(db, 'appOpens', `${userId}_${day}`),
        { userId, day, lastOpenedAt: Timestamp.now() },
        { merge: true },
      );
    } catch (error) {
      console.error('Error recording app open (non-fatal):', error);
    }
  },

  async deleteProject(projectId: string) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const projectRef = doc(db, 'projects', projectId);
      await deleteDoc(projectRef);
      return projectId;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  // Subscribe to user's projects
  subscribeToProjects(userId: string, callback: (projects: Project[]) => void, onError?: (error: Error) => void) {
    const q = query(
      projectsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const projects: Project[] = [];
        snapshot.forEach((doc) => {
          projects.push(projectFromDoc(doc));
        });
        callback(projects);
      },
      (error) => {
        console.error('Error in projects snapshot listener:', error);
        if (onError) {
          onError(error);
        }
        // Call callback with empty array on error to prevent UI from hanging
        callback([]);
      }
    );
  },

  /**
   * One-shot read of a user's projects. The live app uses
   * subscribeToProjects; request/response callers (the MCP server) need a
   * promise, not a subscription.
   */
  async getProjects(userId: string): Promise<Project[]> {
    const q = query(
      projectsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(projectFromDoc);
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }
};

// Re-export types for convenience
export type { User } from 'firebase/auth';